import { useState } from 'react';
import PromptTemplatesLibrary from './PromptTemplatesLibrary';

export interface SceneItem {
  id: string;
  description: string;
  duration: string;
  cameraAngle: string;
  mood: string;
  transition: string;
}

const CAMERA_ANGLES = ['Wide Shot', 'Close-Up', 'Medium Shot', 'POV', 'Overhead', 'Dutch Angle', 'Tracking', 'Dolly', 'Crane'];
const MOODS = ['Dramatic', 'Uplifting', 'Tense', 'Romantic', 'Mysterious', 'Energetic', 'Melancholic', 'Cinematic', 'Playful'];
const TRANSITIONS = ['Cut', 'Dissolve', 'Fade', 'Whip Pan', 'Match Cut', 'Smash Cut', 'Jump Cut'];
const SCENE_DURATIONS = ['2s', '3s', '5s', '8s', '10s', '15s', '20s'];

function makeScene(): SceneItem {
  return {
    id: `scene-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    description: '',
    duration: '5s',
    cameraAngle: 'Wide Shot',
    mood: 'Cinematic',
    transition: 'Cut',
  };
}

interface VideoScriptEditorProps {
  scenes: SceneItem[];
  onScenesChange: (scenes: SceneItem[]) => void;
  onGenerateFromScript: (stitchedPrompt: string) => void;
  darkMode: boolean;
}

function stitchScenes(scenes: SceneItem[]): string {
  const filled = scenes.filter(s => s.description.trim());
  if (filled.length === 0) return '';
  const parts = filled.map((s, i) => {
    const parts: string[] = [];
    parts.push(`[Scene ${i + 1} — ${s.cameraAngle.toUpperCase()}, ${s.duration}, ${s.mood}]`);
    parts.push(s.description.trim());
    if (i < filled.length - 1) {
      parts.push(`[${s.transition} to Scene ${i + 2}]`);
    }
    return parts.join(' ');
  });
  return parts.join(' ');
}

export default function VideoScriptEditor({ scenes, onScenesChange, onGenerateFromScript, darkMode }: VideoScriptEditorProps) {
  const [expandedId, setExpandedId] = useState<string | null>(scenes[0]?.id ?? null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  const base = darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900';
  const cardBg = darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200';
  const inputCls = `w-full text-sm rounded-lg border px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all ${
    darkMode ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  }`;
  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const selectCls = `text-xs rounded-lg border px-2 py-1.5 cursor-pointer outline-none transition-all ${
    darkMode ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-white border-gray-200 text-gray-700'
  }`;

  const updateScene = (id: string, patch: Partial<SceneItem>) => {
    onScenesChange(scenes.map(s => s.id === id ? { ...s, ...patch } : s));
  };

  const addScene = () => {
    const ns = makeScene();
    onScenesChange([...scenes, ns]);
    setExpandedId(ns.id);
    setTimeout(() => {
      const el = document.getElementById(`scene-card-${ns.id}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  const removeScene = (id: string) => {
    const next = scenes.filter(s => s.id !== id);
    onScenesChange(next.length ? next : [makeScene()]);
    if (expandedId === id) setExpandedId(next[0]?.id ?? null);
  };

  const moveScene = (id: string, dir: -1 | 1) => {
    const idx = scenes.findIndex(s => s.id === id);
    if (idx < 0) return;
    const next = [...scenes];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    onScenesChange(next);
  };

  const stitched = stitchScenes(scenes);
  const filledCount = scenes.filter(s => s.description.trim()).length;

  return (
    <div className={`rounded-2xl border overflow-hidden ${base}`}>
      {/* Header */}
      <div className="px-5 py-4 border-b border-inherit">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <i className="ri-file-list-3-line text-red-500 text-base"></i>
            <span className="text-sm font-bold">Video Script</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
              darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
            }`}>{scenes.length} scene{scenes.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowTemplates(true)}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                darkMode
                  ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50'
                  : 'bg-red-50 text-red-600 hover:bg-red-100'
              }`}
            >
              <i className="ri-layout-grid-line text-xs"></i>
              Templates
            </button>
            <button
              onClick={() => setPreviewOpen(p => !p)}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                previewOpen
                  ? darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-50 text-red-600'
                  : darkMode ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <i className="ri-eye-line text-xs"></i>
              Preview
            </button>
          </div>
        </div>
        <p className={`text-[11px] ${muted}`}>
          Write scene by scene — we&apos;ll stitch them into an enhanced prompt when you generate
        </p>
      </div>

      {/* Preview panel */}
      {previewOpen && (
        <div className={`px-5 py-3 border-b ${darkMode ? 'bg-gray-900/60 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
          <p className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Stitched Prompt Preview
          </p>
          {stitched ? (
            <p className={`text-xs leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {stitched}
            </p>
          ) : (
            <p className={`text-xs italic ${muted}`}>Add scene descriptions to see the stitched prompt</p>
          )}
        </div>
      )}

      {/* Scene list */}
      <div className="p-4 space-y-2.5 max-h-[480px] overflow-y-auto">
        {scenes.map((scene, idx) => {
          const isExpanded = expandedId === scene.id;
          return (
            <div
              key={scene.id}
              id={`scene-card-${scene.id}`}
              className={`rounded-xl border transition-all duration-200 overflow-hidden ${cardBg} ${
                isExpanded
                  ? darkMode ? 'border-red-700' : 'border-red-300'
                  : darkMode ? 'hover:border-gray-600' : 'hover:border-gray-300'
              }`}
            >
              {/* Scene header */}
              <button
                onClick={() => setExpandedId(isExpanded ? null : scene.id)}
                className="w-full flex items-center gap-3 px-3.5 py-2.5 text-left cursor-pointer"
              >
                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                  scene.description.trim()
                    ? 'bg-gradient-to-br from-red-500 to-orange-500 text-white'
                    : darkMode ? 'bg-gray-700 text-gray-500' : 'bg-gray-200 text-gray-400'
                }`}>
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  {scene.description.trim() ? (
                    <p className={`text-xs font-medium truncate ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                      {scene.description.slice(0, 60)}{scene.description.length > 60 ? '…' : ''}
                    </p>
                  ) : (
                    <p className={`text-xs italic ${muted}`}>Describe this scene…</p>
                  )}
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
                      {scene.cameraAngle}
                    </span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
                      {scene.duration}
                    </span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
                      {scene.mood}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-0.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
                  <button
                    onClick={() => moveScene(scene.id, -1)}
                    disabled={idx === 0}
                    className={`w-6 h-6 flex items-center justify-center rounded transition-colors cursor-pointer ${
                      idx === 0 ? 'opacity-20 cursor-not-allowed' : darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
                    } ${muted}`}
                  >
                    <i className="ri-arrow-up-s-line text-xs"></i>
                  </button>
                  <button
                    onClick={() => moveScene(scene.id, 1)}
                    disabled={idx === scenes.length - 1}
                    className={`w-6 h-6 flex items-center justify-center rounded transition-colors cursor-pointer ${
                      idx === scenes.length - 1 ? 'opacity-20 cursor-not-allowed' : darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
                    } ${muted}`}
                  >
                    <i className="ri-arrow-down-s-line text-xs"></i>
                  </button>
                  <button
                    onClick={() => removeScene(scene.id)}
                    className={`w-6 h-6 flex items-center justify-center rounded transition-colors text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 cursor-pointer`}
                  >
                    <i className="ri-delete-bin-line text-xs"></i>
                  </button>
                  <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-xs ml-1 ${muted}`}></i>
                </div>
              </button>

              {/* Expanded editor */}
              {isExpanded && (
                <div className={`px-3.5 pb-3.5 space-y-3 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} pt-3`}>
                  {/* Description */}
                  <div>
                    <label className={`block text-[11px] font-semibold mb-1.5 ${muted}`}>
                      Scene Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={scene.description}
                      onChange={e => updateScene(scene.id, { description: e.target.value.slice(0, 300) })}
                      placeholder="Describe what happens in this scene — actions, subjects, environment, lighting..."
                      rows={3}
                      className={`${inputCls} resize-none`}
                    />
                    <p className={`text-right text-[10px] mt-0.5 ${muted}`}>{scene.description.length}/300</p>
                  </div>

                  {/* Controls row 1 */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className={`block text-[11px] font-semibold mb-1 ${muted}`}>Camera Angle</label>
                      <select
                        value={scene.cameraAngle}
                        onChange={e => updateScene(scene.id, { cameraAngle: e.target.value })}
                        className={`${selectCls} w-full`}
                      >
                        {CAMERA_ANGLES.map(a => <option key={a} value={a}>{a}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={`block text-[11px] font-semibold mb-1 ${muted}`}>Duration</label>
                      <select
                        value={scene.duration}
                        onChange={e => updateScene(scene.id, { duration: e.target.value })}
                        className={`${selectCls} w-full`}
                      >
                        {SCENE_DURATIONS.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Controls row 2 */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className={`block text-[11px] font-semibold mb-1 ${muted}`}>Mood</label>
                      <select
                        value={scene.mood}
                        onChange={e => updateScene(scene.id, { mood: e.target.value })}
                        className={`${selectCls} w-full`}
                      >
                        {MOODS.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={`block text-[11px] font-semibold mb-1 ${muted}`}>Transition Out</label>
                      <select
                        value={scene.transition}
                        onChange={e => updateScene(scene.id, { transition: e.target.value })}
                        className={`${selectCls} w-full`}
                        disabled={idx === scenes.length - 1}
                      >
                        {TRANSITIONS.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Add scene */}
        <button
          onClick={addScene}
          className={`w-full py-2.5 rounded-xl border-2 border-dashed text-xs font-semibold flex items-center justify-center gap-2 transition-all cursor-pointer ${
            darkMode
              ? 'border-gray-600 text-gray-500 hover:border-red-600 hover:text-red-400'
              : 'border-gray-300 text-gray-400 hover:border-red-300 hover:text-red-500'
          }`}
        >
          <i className="ri-add-line text-sm"></i>
          Add Scene
        </button>
      </div>

      {/* Footer: Generate from Script */}
      <div className={`px-4 pb-4 border-t pt-3 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        {filledCount > 0 ? (
          <button
            onClick={() => onGenerateFromScript(stitched)}
            className="w-full py-3 bg-gradient-to-r from-red-500 to-orange-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-red-500/30 transition-all flex items-center justify-center gap-2 cursor-pointer"
          >
            <i className="ri-magic-line text-base"></i>
            Generate from Script
            <span className={`text-[11px] font-normal px-2 py-0.5 bg-white/20 rounded-full`}>
              {filledCount} scene{filledCount !== 1 ? 's' : ''}
            </span>
          </button>
        ) : (
          <div className={`text-center py-2 text-xs ${muted}`}>
            <i className="ri-information-line mr-1"></i>
            Fill in at least one scene description to generate
          </div>
        )}
      </div>

      <PromptTemplatesLibrary
        isOpen={showTemplates}
        onClose={() => setShowTemplates(false)}
        onLoadTemplate={(newScenes) => {
          onScenesChange(newScenes);
          setExpandedId(newScenes[0]?.id ?? null);
        }}
        darkMode={darkMode}
      />
    </div>
  );
}

export { makeScene };
